// Dependencias
import axios from 'axios';
import { URL_API } from '../config/keys.config';

// Utils
import { httpTokenInterceptor } from '../utils/httpTokenInterceptor';

// Instancias
const token = document.querySelector<HTMLInputElement>('#access-token')!.value;
const clientAxios = axios.create({
  baseURL: URL_API,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  }
});

clientAxios.interceptors.response.use((res) => res, httpTokenInterceptor);

/**
 * Indicadores generales de la API pública
 */
export async function fetchApiSummary() {
  const { data } = await clientAxios.get<ApiKeysSummary>('/panel/api-keys/summary');
  return data;
}

/**
 * Listado paginado de API keys
 */
export async function fetchApiKeys({ page = 1, limit = 10, status, search }: FetchApiKeysOptions) {
  const params: Record<string, string | number> = { page, limit };
  if (status && status !== 'all') params.status = status;
  if (search) params.search = search;

  const { data } = await clientAxios.get<ApiKeysResponse>('/panel/api-keys', { params });
  return data;
}

/**
 * Detalle de una API key y su uso
 */
export async function fetchApiKeyDetail(id: number, days = 30) {
  const { data } = await clientAxios.get<ApiKeyDetailResponse>(`/panel/api-keys/${id}`, { params: { days } });
  return data;
}

/**
 * Detalle de un usuario de la API, sus keys y su uso
 */
export async function fetchApiUserDetail(id: number, days = 30) {
  const { data } = await clientAxios.get<ApiUserDetailResponse>(`/panel/api-users/${id}`, { params: { days } });
  return data;
}

/**
 * Revoca una API key (motivo obligatorio)
 */
export async function revokeApiKey(id: number, reason: string) {
  const { data } = await clientAxios.patch<{ message: string; key: ApiKey }>(`/panel/api-keys/${id}/revoke`, { reason });
  return data;
}

// Definición de tipos e interfaces
export type ApiKeyStatus = 'active' | 'revoked' | 'expired';

export interface ApiKey {
  id: number;
  user_id: number;
  name: string;
  key_prefix: string;
  environment: 'live' | 'test';
  scopes: string;
  allowed_ips: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  created_at: string;
  created_by: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_reason: string | null;
  status: ApiKeyStatus;
  user_name: string | null;
  user_last_name: string | null;
  user_email: string | null;
  user_api: number | null;
  user_blocked: number | null;
}

export interface ApiKeysSummary {
  usersWithApi: number;
  activeKeys: number;
  totalKeys: number;
  requests24h: number;
  errors24h: number;
  errorRate24h: number;
}

export interface ApiKeysResponse {
  keys: ApiKey[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

export interface ApiUsage {
  days: number;
  daily: { day: string; total: number; s2xx: number; s4xx: number; s5xx: number }[];
  topRoutes: { method: string; route: string; total: number; errors: number; avgDurationMs: number }[];
  recent: {
    id: number;
    api_key_id: number;
    key_prefix: string | null;
    request_id: string;
    method: string;
    route: string;
    document_id: number | null;
    status_code: number;
    duration_ms: number;
    ip: string;
    created_at: string;
  }[];
}

export interface ApiUser {
  id: number;
  name: string | null;
  last_name: string | null;
  email: string;
  api: number;
  blocked: number;
  plan: string | null;
}

export interface ApiKeyDetailResponse {
  key: ApiKey;
  usage: ApiUsage;
}

export interface ApiUserDetailResponse {
  user: ApiUser;
  keys: ApiKey[];
  usage: ApiUsage;
}

type FetchApiKeysOptions = {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
};
